/**
 * Canonical UI language name → ISO code map. Single source of truth — import
 * this instead of redefining the map (services/api, displayLanguage, etc.).
 */
export const LANGUAGE_CODES: Record<string, string> = {
  English: 'en',
  Telugu: 'te',
  Kannada: 'kn',
  Tamil: 'ta',
  Hindi: 'hi',
  Malayalam: 'ml',
  Oriya: 'or',
  Bengali: 'bn',
  Punjabi: 'pa',
  Gujarati: 'gu',
};

/**
 * Language codes in the order the API's per-language *bitmask* fields use —
 * Telugu first, NOT the display order of LANGUAGE_CODES above.
 *
 * The server builds `autoTranslatedFlags` by walking its own language list
 * (jnana-serverless/src/libs/types/languages.ts) and writing '1' at a
 * language's index when AWS Translate produced that translation. The same
 * order generates the `${code}Text` fields in config/nodeTypes.ts. Reading a
 * mask against the wrong order silently mislabels which facts are machine
 * translated, so languages.test.ts pins this against the field definitions.
 */
export const LANGUAGE_BITMASK_ORDER = [
  'te', 'en', 'kn', 'ta', 'hi', 'ml', 'or', 'bn', 'pa', 'gu',
] as const;

/**
 * Decode one language's bit out of a per-language flag mask. Returns false for
 * anything that is not a well-formed mask covering the requested language —
 * absent, truncated by bad data, or a language the mask predates.
 */
export const isFlaggedForLanguage = (
  mask: unknown,
  languageCode: string
): boolean => {
  if (typeof mask !== 'string') return false;
  const index = LANGUAGE_BITMASK_ORDER.indexOf(
    languageCode as (typeof LANGUAGE_BITMASK_ORDER)[number]
  );
  if (index < 0) return false;
  if (mask.length !== LANGUAGE_BITMASK_ORDER.length) return false;
  return mask[index] === '1';
};

/** Resolve a language name ("Telugu") or code ("te") to a code; defaults to 'en'. */
export const toLanguageCode = (nameOrCode: string | null | undefined): string => {
  if (!nameOrCode) return 'en';
  return LANGUAGE_CODES[nameOrCode] ?? nameOrCode;
};
